import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UsagePill } from "@/components/UsagePill";
import { AlertTriangle, ArrowRight, Camera, Eraser, Image as ImageIcon, Keyboard, MessageSquareText, Settings2, Wand2, X } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Header } from "@/components/Header";
import { ScreenshotUploader } from "@/components/dashboard/ScreenshotUploader";
import { PasteInput, QuickInput } from "@/components/dashboard/TextInputs";
import { TranscriptEditor } from "@/components/dashboard/TranscriptEditor";
import { StyleControls } from "@/components/dashboard/StyleControls";
import { RizzControls, RizzToggle } from "@/components/dashboard/RizzControls";
import { DEFAULT_RIZZ } from "@/lib/config";
import { NotesInput } from "@/components/dashboard/NotesInput";
import { Results } from "@/components/dashboard/Results";
import { SAMPLE_KEY } from "@/components/landing/useStart";
import { api, errMsg, newRequestId } from "@/lib/api";
import { dedupeAdjacent, withIds } from "@/lib/chat";

const lastThemId = (msgs) => [...msgs].reverse().find((m) => m.speaker === "them")?.id || null;

export default function Dashboard() {
  const [config, setConfig] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [usage, setUsage] = useState(null);
  const [quotaHit, setQuotaHit] = useState(false);
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [latestId, setLatestId] = useState(null);
  const [ocrMeta, setOcrMeta] = useState({ ambiguous: false, notes: "" });
  const [language, setLanguage] = useState("auto");
  const [intensity, setIntensity] = useState("subtle");
  const [personality, setPersonality] = useState("calm");
  const [notes, setNotes] = useState("");
  const [result, setResult] = useState(null);
  const [genState, setGenState] = useState("idle");
  const [genError, setGenError] = useState("");
  const [regenIdx, setRegenIdx] = useState(null);
  const [lastTweak, setLastTweak] = useState("");
  const [ready, setReady] = useState(false);
  const [rizzOn, setRizzOn] = useState(false);
  const [rizz, setRizz] = useState(DEFAULT_RIZZ);
  const historyRef = useRef([]);
  const lastReqRef = useRef(null);
  const ctrlRef = useRef(null);

  const loadConversation = useCallback((msgs, latest = null, meta = { ambiguous: false, notes: "" }) => {
    const clean = dedupeAdjacent(msgs);
    setMessages(clean);
    setLatestId(latest && clean.some((m) => m.id === latest) ? latest : lastThemId(clean));
    setOcrMeta(meta);
    setResult(null);
    setGenState("idle");
  }, []);

  useEffect(() => {
    Promise.all([api.get("/config"), api.get("/user-preferences"), api.get("/conversation-session"), api.get("/usage")])
      .then(([c, p, s, u]) => {
        setConfig(c.data);
        setPrefs(p.data);
        setUsage(u.data);
        setLanguage(p.data.language);
        if (p.data.language !== "auto") setRizz((r) => ({ ...r, language: p.data.language }));
        setIntensity(p.data.intensity);
        setPersonality(p.data.personality);
        const sample = sessionStorage.getItem(SAMPLE_KEY);
        if (sample) {
          sessionStorage.removeItem(SAMPLE_KEY);
          loadConversation(withIds(JSON.parse(sample)));
          toast.success("Sample chat loaded — hit Generate.");
        } else if (s.data.messages?.length) {
          const restored = withIds(s.data.messages);
          const li = s.data.latest_index;
          loadConversation(restored, li != null ? restored[li]?.id : null);
        }
      })
      .catch((e) => toast.error(errMsg(e, "Couldn't load your workspace.")))
      .finally(() => setReady(true));
  }, [loadConversation]);

  const latestIndex = useMemo(() => {
    const clean = messages.filter((m) => m.text.trim());
    const idx = clean.findIndex((m) => m.id === latestId);
    return { clean, idx };
  }, [messages, latestId]);

  useEffect(() => {
    if (!ready || !prefs || prefs.retention === "none") return;
    const t = setTimeout(() => {
      const { clean, idx } = latestIndex;
      api.put("/conversation-session", { messages: clean.map(({ speaker, text }) => ({ speaker, text })), latest_index: idx >= 0 ? idx : null }).catch(() => {});
    }, 900);
    return () => clearTimeout(t);
  }, [latestIndex, ready, prefs]);

  useEffect(() => {
    if (latestId && !messages.some((m) => m.id === latestId && m.speaker !== "me")) setLatestId(lastThemId(messages));
  }, [messages, latestId]);

  const request = async (extra) => {
    const { clean, idx } = latestIndex;
    ctrlRef.current?.abort();
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    const { data } = await api.post("/generate-replies", {
      messages: clean.map(({ speaker, text }) => ({ speaker, text })),
      latest_index: idx >= 0 ? idx : null,
      language, intensity, personality, notes: notes.trim(),
      rizz: rizzOn ? (({ prevLanguage, ...r }) => r)(rizz) : null,
      ...extra,
    }, { signal: ctrl.signal, timeout: 90000 });
    historyRef.current = [...data.replies.map((r) => r.text), ...historyRef.current].slice(0, 12);
    if (data.usage) setUsage(data.usage);
    return data;
  };

  const generate = async (tweak = "", retry = false) => {
    if (!latestIndex.clean.length) return toast.error("Add the conversation first.");
    if (latestIndex.clean.some((m) => m.speaker === "unknown")) return toast.error("Label every message as Them or Me first.");
    if (latestIndex.idx < 0 && !latestIndex.clean.some((m) => m.speaker === "them")) return toast.error("Mark which of their messages you're replying to.");
    if (rizzOn && rizz.style === "custom" && !rizz.custom_tone.trim()) return toast.error("Describe the tone you want for Custom Rizz.");
    setLastTweak(tweak);
    setGenState("loading");
    setGenError("");
    try {
      const payload = retry && lastReqRef.current ? lastReqRef.current : { count: 3, tone_tweak: tweak, avoid: historyRef.current.slice(0, 9), request_id: newRequestId() };
      lastReqRef.current = payload;
      setResult(await request(payload));
      setGenState("idle");
    } catch (e) {
      const msg = errMsg(e, "Couldn't generate replies.");
      if (msg === "Cancelled.") { setGenState(result ? "idle" : "idle"); return; }
      setQuotaHit(e?.response?.status === 402);
      if (e?.response?.status === 402) api.get("/usage").then((u) => setUsage(u.data)).catch(() => {});
      setGenError(msg);
      setGenState("error");
    }
  };

  const regenerateOne = async (i) => {
    setRegenIdx(i);
    try {
      const data = await request({ count: 1, tone_tweak: lastTweak, request_id: newRequestId(), avoid: [...new Set([...result.replies.map((r) => r.text), ...historyRef.current])].slice(0, 12) });
      setResult((r) => ({ ...r, replies: r.replies.map((x, j) => (j === i ? data.replies[0] : x)) }));
    } catch (e) {
      const msg = errMsg(e, "Couldn't regenerate that reply.");
      if (e?.response?.status === 402) toast.error(msg, { action: { label: "Upgrade", onClick: () => navigate("/upgrade") } });
      else if (msg !== "Cancelled.") toast.error(msg);
    } finally {
      setRegenIdx(null);
    }
  };

  const editReply = (i, text) => setResult((r) => ({ ...r, replies: r.replies.map((x, j) => (j === i ? { ...x, text } : x)) }));

  const clearAll = async () => {
    ctrlRef.current?.abort();
    setFiles([]);
    loadConversation([]);
    setNotes("");
    try {
      await api.delete("/conversation-session");
      toast.success("Conversation cleared.");
    } catch (e) {
      toast.error(errMsg(e, "Cleared locally, but the server copy couldn't be deleted. Please retry."));
    }
  };

  const onExtracted = (data) => { loadConversation(withIds(data.messages), null, { ambiguous: data.ambiguous, notes: data.notes }); };

  return (
    <div className="min-h-screen" data-testid="dashboard-page">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Reply generator</h1>
            <p className="text-sm text-muted-foreground mt-1">Add the chat → check it → pick a vibe → get three replies.</p>
          </div>
          <div className="flex items-center gap-2">
            <UsagePill usage={usage} />
            <Link to="/settings" data-testid="dashboard-settings-link" className="h-9 w-9 rounded-full border border-border flex items-center justify-center hover:bg-secondary" aria-label="Settings"><Settings2 className="h-4 w-4" /></Link>
            <button data-testid="clear-conversation-button" onClick={clearAll} className="h-9 px-4 rounded-full border border-border text-xs font-semibold inline-flex items-center gap-1.5 hover:border-rose-500/60 hover:text-rose-500 transition-colors"><Eraser className="h-3.5 w-3.5" />Clear conversation</button>
          </div>
        </div>

        <Link to="/photo-rizz" data-testid="dashboard-photo-rizz-banner"
          className="mb-6 flex items-center gap-4 rounded-3xl border border-primary/40 bg-primary/10 p-4 sm:p-5 hover:border-primary transition-colors group">
          <span className="h-12 w-12 shrink-0 rounded-2xl brand-gradient text-white flex items-center justify-center"><Camera className="h-5 w-5" /></span>
          <span className="flex-1">
            <span className="block font-bold">Photo Rizz — start a chat from a photo</span>
            <span className="block text-xs text-muted-foreground">Upload a girl's or guy's photo and get flirty opener lines you can copy and send.</span>
          </span>
          <ArrowRight className="h-5 w-5 text-primary group-hover:translate-x-1 transition-transform" />
        </Link>

        {config && !config.ai_live && (
          <div data-testid="demo-mode-banner" className="mb-6 flex gap-2 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
            <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" /> DEMO MODE: live AI credentials aren't configured on this server, so screenshot reading and reply generation are unavailable.
          </div>
        )}

        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-6 items-start">
          <div className="space-y-6">
            <div className="panel p-5 sm:p-6">
              <Tabs defaultValue="screenshot">
                <TabsList className="grid grid-cols-3 w-full rounded-2xl h-11 mb-5">
                  <TabsTrigger value="screenshot" data-testid="tab-upload-screenshot" className="rounded-xl text-xs sm:text-sm gap-1.5"><ImageIcon className="h-3.5 w-3.5" />Screenshot</TabsTrigger>
                  <TabsTrigger value="paste" data-testid="tab-paste-text" className="rounded-xl text-xs sm:text-sm gap-1.5"><MessageSquareText className="h-3.5 w-3.5" />Paste chat</TabsTrigger>
                  <TabsTrigger value="quick" data-testid="tab-quick-message" className="rounded-xl text-xs sm:text-sm gap-1.5"><Keyboard className="h-3.5 w-3.5" />Type it</TabsTrigger>
                </TabsList>
                <TabsContent value="screenshot"><ScreenshotUploader files={files} setFiles={setFiles} onExtracted={onExtracted} config={config} /></TabsContent>
                <TabsContent value="paste"><PasteInput onParsed={(m) => loadConversation(m)} /></TabsContent>
                <TabsContent value="quick"><QuickInput onParsed={(m, l) => loadConversation(m, l)} /></TabsContent>
              </Tabs>
            </div>
            <TranscriptEditor messages={messages} setMessages={setMessages} latestId={latestId} setLatestId={setLatestId} ambiguous={ocrMeta.ambiguous} notes={ocrMeta.notes} />
          </div>

          <div className="space-y-6 lg:sticky lg:top-24">
            <RizzToggle on={rizzOn} onToggle={() => setRizzOn((v) => !v)} />
            {rizzOn ? (
              <RizzControls rizz={rizz} setRizz={setRizz} />
            ) : (
              <StyleControls language={language} setLanguage={setLanguage} intensity={intensity} setIntensity={setIntensity} personality={personality} setPersonality={setPersonality} />
            )}
            <div className="panel p-5 sm:p-6 space-y-4">
              <NotesInput value={notes} onChange={setNotes} voiceAvailable={!!config?.voice_available} />
              <div className="flex gap-2">
                <button data-testid="generate-replies-submit-button" onClick={() => generate()} disabled={genState === "loading" || !messages.length || (config && !config.ai_live)}
                  className="flex-1 h-13 py-3.5 rounded-full brand-gradient text-white font-semibold inline-flex items-center justify-center gap-2 shadow-lg shadow-purple-500/25 disabled:opacity-40 hover:opacity-95 transition-opacity">
                  <Wand2 className="h-4 w-4" />{rizzOn ? "Generate rizz" : result ? "Generate new replies" : "Generate my replies"}
                </button>
                {genState === "loading" && (
                  <button data-testid="cancel-generate-button" onClick={() => { ctrlRef.current?.abort(); setGenState("idle"); }} className="h-13 px-4 rounded-full border border-border" aria-label="Cancel"><X className="h-4 w-4" /></button>
                )}
              </div>
            </div>
            <Results state={genState} error={genError} result={result} onRetry={() => generate(lastTweak, true)} onTweak={(t) => generate(t)}
              quota={quotaHit} onRegenerate={regenerateOne} onEdit={editReply} regenIdx={regenIdx} voice={prefs?.tts_voice || "nova"} voiceAvailable={!!config?.voice_available} />
          </div>
        </div>
      </main>
    </div>
  );
}
